import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ModalShell, ModalActions } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface ButtonConfig {
  text: string;
  onPress: () => void;
  loading?: boolean;
}

interface ConfirmationModalProps {
  visible: boolean;
  title: string;
  message: string;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  iconName?: React.ComponentType<any>;
  showCloseButton?: boolean;
  onClose?: () => void;
  variant?: 'single' | 'two';
  /** Destruktive Bestätigung: roter Primärbutton + Alert-Rolle (z. B. Stornieren/Löschen). */
  destructive?: boolean;
  button?: ButtonConfig;
  buttons?: {
    cancel: ButtonConfig;
    confirm: ButtonConfig;
  };
}

/** Rendert Inline-Fettschrift: `**wichtig**` -> hervorgehobenes <strong>. */
const renderInline = (text: string): React.ReactNode =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-semibold text-gray-900 dark:text-white">
        {part.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );

const DEFAULT_ICON: Record<NonNullable<ConfirmationModalProps['type']>, React.ComponentType<any>> = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationCircleIcon,
  question: QuestionMarkCircleIcon,
  info: InformationCircleIcon,
};

// Icon-Kachelton semantisch aus dem Typ ableiten (statt pauschal Marke/Grün).
const TONE_BY_TYPE: Record<NonNullable<ConfirmationModalProps['type']>, 'accent' | 'danger' | 'warning' | 'info'> = {
  success: 'accent',
  warning: 'warning',
  error: 'danger',
  question: 'accent',
  info: 'info',
};

/**
 * Bestätigungs-/Meldungsdialog (Issue #11, Variante `confirm`).
 * Nutzt intern die zentrale ModalShell; die öffentliche API bleibt unverändert,
 * damit alle bestehenden Aufrufer weiter funktionieren.
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  visible,
  title,
  message,
  type = 'info',
  iconName,
  showCloseButton,
  onClose,
  variant = 'single',
  destructive = false,
  button,
  buttons,
}) => {
  const Icon = iconName || DEFAULT_ICON[type];
  const isDanger = destructive || type === 'error';
  const tone = TONE_BY_TYPE[type];

  // Backdrop-/Escape-Schließen bildet die bisherige Logik ab:
  // X-Button -> onClose (Fallback: sichere Aktion); Backdrop/Escape -> primäre
  // (single) bzw. Cancel-Aktion (two).
  const handleClose = (reason: ModalCloseReason) => {
    if (reason === 'button' && onClose) {
      onClose();
      return;
    }
    // X ohne expliziten onClose sowie Backdrop/Escape -> sichere Aktion.
    if (variant === 'single') {
      button?.onPress();
    } else {
      buttons?.cancel.onPress();
    }
  };

  const footer =
    variant === 'single'
      ? button && (
          <ModalActions
            danger={isDanger}
            primary={{ label: button.text, onClick: button.onPress, loading: button.loading }}
          />
        )
      : buttons && (
          <ModalActions
            danger={isDanger}
            secondary={{ label: buttons.cancel.text, onClick: buttons.cancel.onPress }}
            primary={{
              label: buttons.confirm.text,
              onClick: buttons.confirm.onPress,
              loading: buttons.confirm.loading,
            }}
          />
        );

  return (
    <ModalShell
      open={visible}
      onClose={handleClose}
      variant="confirm"
      size="sm"
      accent={isDanger ? 'danger' : 'brand'}
      iconTone={tone}
      icon={Icon}
      title={title}
      hideCloseButton={showCloseButton === false}
      footer={footer}
    >
      <div className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {message.split('\n').map((line, index) => {
          if (line.startsWith('•')) {
            const [dogName, ...rest] = line.substring(2).split(':');
            if (rest.length > 0) {
              return (
                <div key={index} className="mb-2 flex items-start text-left">
                  <span className="mr-2 mt-0.5 text-gray-400 dark:text-gray-500">•</span>
                  <div>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{dogName}:</span>
                    <span className="text-gray-600 dark:text-gray-300">{rest.join(':')}</span>
                  </div>
                </div>
              );
            }
            return (
              <p key={index} className="mb-2 text-left">
                {renderInline(line)}
              </p>
            );
          }
          // Explizites **Fett** übersteuert die automatische Ganzzeilen-Fettung (Frage-Zeilen).
          const autoBold = line.includes('?') && !line.includes('**');
          return (
            <p
              key={index}
              className={`${index > 0 ? 'mt-3' : ''} ${
                autoBold ? 'font-semibold text-gray-800 dark:text-gray-200' : ''
              }`}
            >
              {renderInline(line)}
            </p>
          );
        })}
      </div>
    </ModalShell>
  );
};

export default ConfirmationModal;
